import { isEmpty } from 'lodash-es'

import { SearchRequestKeys } from '../../mapper/CmsPropertyMapper.enum'
import { EstateType } from '../../mapper/decoratedEstateDetailMapper.enum'
import { EsrKey, EsrType, EstateTypeLabel, FilterId, OfferType, Unit } from '../../service/realEstateService.enum'
import {
    ESRType,
    FilterValue,
    Filters,
    RealEstateDetailResponse,
    SearchFilters,
    SearchRequest,
} from '../../service/realEstateService.interface'
import { MailAgentTags } from '../../store/mailAgentStoreModule/mailAgentStoreModule.interface'
import { createRangeStr } from '../../utils/stringUtils'
import { MailAgentData, MailAgentSearchParams } from './mailAgentData.interface'

export function buildMailAgentSearchText(tags: MailAgentTags): string {
    const searchText: string[] = []

    if (tags.estateType) {
        searchText.push(tags.estateType)
    }

    if (tags.priceRange) {
        searchText.push(tags.priceRange)
    }

    if (tags.roomRange) {
        searchText.push(tags.roomRange)
    }

    return !isEmpty(searchText) ? searchText.join(', ') : 'Immobilien'
}

/**
 * The buildMailAgentDataForDetail function is designed to generate tags for the mailAgentBanner/Modal and a set of
 * "simulated" search parameters for a detail page that does not inherently support search functionality.
 * These simulated search parameters are created based on the exposé detail information
 */
export function buildMailAgentDataForDetail(estate: RealEstateDetailResponse, locationId: string): MailAgentData {
    return {
        tags: createMailAgentTags(estate),
        searchParams: createMailAgentSearchParams(estate, locationId),
    }
}

/**
 * The buildMailAgentTagsFrom function is designed to generate mailAgentTags for the mailAgentBanner/Modal
 * based on the search request
 */
export function buildMailAgentTagsFrom(
    serviceRequest: Partial<SearchRequest>,
    locationName: string,
    searchFilters: SearchFilters,
): Partial<MailAgentTags> {
    const tags: Partial<MailAgentTags> = {}
    const esrKey: EsrKey =
        serviceRequest[SearchRequestKeys.ESTATE_SELL_RENT] === EsrType.RENT ? EsrKey.RENT : EsrKey.BUY
    const estateTypes = searchFilters[esrKey].estateTypes
    const estateTypeFilters = getFiltersForEstateType(estateTypes)[0]
    const equipmentFilters = estateTypeFilters[FilterId.EQUIPMENT_TYPES].values
    const categoryFilters = estateTypeFilters[FilterId.REAL_ESTATE_CATEGORIES].values
    const estateTypeLabel = getLabelForSelectedEstateType(estateTypes)

    tags.locationName = locationName
    tags.estateType = estateTypeLabel
    tags.searchRadius = createSearchRadiusStr(serviceRequest[SearchRequestKeys.SEARCH_RADIUS]!)

    if (serviceRequest[SearchRequestKeys.PRICE_MIN] || serviceRequest[SearchRequestKeys.PRICE_MAX]) {
        tags.priceRange = createRangeStr(
            serviceRequest[SearchRequestKeys.PRICE_MIN],
            serviceRequest[SearchRequestKeys.PRICE_MAX],
            Unit.EURO,
        )
    }

    if (serviceRequest[SearchRequestKeys.FLOOR_MIN] || serviceRequest[SearchRequestKeys.FLOOR_MAX]) {
        tags.areaRange = createRangeStr(
            serviceRequest[SearchRequestKeys.FLOOR_MIN],
            serviceRequest[SearchRequestKeys.FLOOR_MAX],
            Unit.SQUARE_METERS,
        )
    }

    if (serviceRequest[SearchRequestKeys.ROOM_MIN] || serviceRequest[SearchRequestKeys.ROOM_MAX]) {
        tags.roomRange = createRangeStr(
            serviceRequest[SearchRequestKeys.ROOM_MIN],
            serviceRequest[SearchRequestKeys.ROOM_MAX],
            ` ${Unit.ROOMS}`,
        )
    }

    if (!isEmpty(equipmentFilters)) {
        tags.equipment = findSelectedLabels(equipmentFilters)
    }

    if (!isEmpty(categoryFilters)) {
        tags.category = findSelectedLabels(categoryFilters)
    }

    return tags
}

function getLabelForSelectedEstateType(estateTypes: ESRType[]): string {
    return estateTypes.find((filter: ESRType) => filter.selected)!.label
}

function findSelectedLabels(filters: FilterValue[]): string[] {
    return filters.filter((filter: FilterValue) => filter.selected).map((filter: FilterValue) => filter.label)
}

function getFiltersForEstateType(esrType: ESRType[]): Filters[] {
    return esrType.filter((estateType: ESRType) => estateType.selected).map((estateType: ESRType) => estateType.filters)
}

function createMailAgentTags(estate: RealEstateDetailResponse): Partial<MailAgentTags> {
    const tags: Partial<MailAgentTags> = {}

    tags.locationName = estate.city
    tags.estateType = determineEstateNameTag(estate.offerType, estate.type)
    tags.searchRadius = createSearchRadiusStr('20')

    if (!estate.isPriceOnRequest && estate.price) {
        tags.priceRange = createPriceStr(estate.price)
    }

    // (!) -1 is the value returned by the immowelt API when rooms do not exist
    if (estate.rooms && estate.rooms !== -1) {
        tags.roomRange = createRoomStr(estate.rooms)
    }

    return tags
}

function createMailAgentSearchParams(estate: RealEstateDetailResponse, locationId: string): MailAgentSearchParams {
    return {
        lat: estate.latitude,
        lng: estate.longitude,
        esr: mapOfferTypeToEsr(estate.offerType),
        etype: determineEType(estate.offerType, estate.type),
        prima: determineMaxPrice(estate.isPriceOnRequest, estate.price),
        roomi: estate.rooms,
        location: locationId,
    }
}

function mapOfferTypeToEsr(offerType: OfferType): EsrType {
    switch (offerType) {
        case OfferType.SELL:
            return 1
        case OfferType.RENT:
        case OfferType.WAZ:
            return 2
    }
}

function determineEType(offerType: OfferType, estateType: EstateType): number {
    if (offerType === OfferType.WAZ) {
        return 15
    }

    return mapEstateTypeToEType(estateType)!
}

function mapEstateTypeToEType(estateType: EstateType) {
    switch (estateType) {
        case EstateType.APARTMENT:
            return 1
        case EstateType.HOUSE:
            return 2
        case EstateType.PROPERTY:
            return 3
        case EstateType.OFFICE:
            return 4
        case EstateType.RETAIL_TRADE:
            return 5
        case EstateType.HALL:
            return 6
        case EstateType.GARAGE:
            return 13
    }
}

function determineMaxPrice(isPriceOnRequest: boolean, price: number): number | undefined {
    return !isPriceOnRequest && price ? Math.floor(price * 1.1) : undefined
}

function determineEstateNameTag(offerType: OfferType, estateType: EstateType): EstateTypeLabel | undefined {
    if (offerType === OfferType.WAZ) {
        return EstateTypeLabel.TEMPORARY_RESIDENCE
    }

    switch (estateType) {
        case EstateType.APARTMENT:
            return offerType === OfferType.RENT ? EstateTypeLabel.APARTMENT_RENT : EstateTypeLabel.APARTMENT_SELL
        case EstateType.HOUSE:
            return EstateTypeLabel.HOUSE
        case EstateType.GARAGE:
            return EstateTypeLabel.GARAGE
        case EstateType.PROPERTY:
            return EstateTypeLabel.PROPERTY
        case EstateType.OFFICE:
            return EstateTypeLabel.OFFICE
        case EstateType.HALL:
            return EstateTypeLabel.HALL
        case EstateType.RETAIL_TRADE:
            return EstateTypeLabel.RETAIL_TRADE
    }
}

function createPriceStr(price: number): string {
    const priceFormatted: string = (price * 1.1).toLocaleString('de-DE', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    })

    return `bis ${priceFormatted}`
}

function createRoomStr(rooms: number): string {
    return `ab ${rooms} ${Unit.ROOMS}`
}

function createSearchRadiusStr(radius: string): string {
    return `Umkreis ${radius}${Unit.KM}`
}
